import { useSyncExternalStore } from "react";

// `storage` only fires in OTHER tabs, so writes made by this tab are broadcast
// through a custom event to keep subscribers in sync (mirrors use-local-storage).
const SESSION_STORAGE_UPDATE_EVENT = "session-storage-update";

// Storage access throws outright in some privacy modes, so reads and writes are
// guarded: a caller falls back to "nothing stored" instead of crashing a render.
export const readSessionStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

// Use instead of `sessionStorage.setItem` whenever a `useSessionStorage`
// subscriber has to react to the write within the same tab.
export const writeSessionStorage = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // A blocked write is not worth failing the interaction over — the value is
    // a convenience. Subscribers are still notified and re-read the old value.
  }
  window.dispatchEvent(new Event(SESSION_STORAGE_UPDATE_EVENT));
};

// Exported so a store keyed on a single constant can create its subscribe
// function once — useSyncExternalStore resubscribes whenever it changes identity.
export const subscribeSessionStorage =
  (key: string) => (callback: () => void) => {
    const handler = (e: StorageEvent) => {
      if (e.key === key) callback();
    };

    window.addEventListener("storage", handler);
    window.addEventListener(SESSION_STORAGE_UPDATE_EVENT, callback);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener(SESSION_STORAGE_UPDATE_EVENT, callback);
    };
  };

export const useSessionStorage = (key: string) => {
  return useSyncExternalStore(subscribeSessionStorage(key), () =>
    readSessionStorage(key)
  );
};
